import api from './api';

// ─── TypeScript Interfaces ────────────────────────────────────────────────────

export type PaymentStatus = 'pending' | 'approved' | 'rejected' | 'failed' | 'refunded';
export type OrderStatus = 'pending' | 'paid' | 'expired' | 'cancelled';
export type SubscriptionStatus = 'active' | 'expired' | 'cancelled';
export type PaymentMethod = 'upi' | 'bank_transfer';

// Pagination metadata returned by list endpoints
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ─── Order Types ──────────────────────────────────────────────────────────────

export interface CreateOrderRequest {
  planId: number;
  userId: number;
}

export interface OrderData {
  orderId: string;
  planName: string;
  amount: number;
  billingDuration: string;
  createdAt: string;
}

export interface OrderDetails extends OrderData {
  userId: number;
  planId: number;
  status: OrderStatus;
}

export interface CreateOrderResponse {
  success: boolean;
  data: OrderData;
}

export interface GetOrderResponse {
  success: boolean;
  data: OrderDetails;
}

// ─── Payment Types ────────────────────────────────────────────────────────────

export interface SubmitPaymentRequest {
  orderId: string;
  transactionId: string;
  paymentMethod: PaymentMethod;
  paymentProof?: File;
}

export interface SubmitPaymentResponse {
  success: boolean;
  message: string;
  data: {
    paymentId: number;
    status: 'pending';
    submittedAt: string;
  };
}

export interface PaymentRecord {
  id: number;
  orderId: string;
  planName: string;
  amount: number;
  transactionId: string;
  status: PaymentStatus;
  submittedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
}

export interface GetUserPaymentsParams {
  page?: number;
  limit?: number;
  status?: PaymentStatus;
}

export interface GetUserPaymentsResponse {
  success: boolean;
  data: {
    payments: PaymentRecord[];
    pagination: PaginationMeta;
  };
}

// ─── Admin Payment Types ──────────────────────────────────────────────────────

export interface AdminPaymentRecord {
  id: number;
  orderId: string;
  user: {
    id: number;
    name: string;
    email: string;
  };
  plan: {
    id: number;
    name: string;
    price: number;
  };
  amount: number;
  transactionId: string;
  paymentProofUrl?: string;
  status: PaymentStatus;
  amountMismatch: boolean;
  submittedAt: string;
}

export interface GetAllPaymentsParams {
  page?: number;
  limit?: number;
  status?: PaymentStatus;
  sortBy?: string;
  order?: 'ASC' | 'DESC';
}

export interface GetAllPaymentsResponse {
  success: boolean;
  data: {
    payments: AdminPaymentRecord[];
    pagination: PaginationMeta;
  };
}

export interface ApprovePaymentRequest {
  adminId: number;
}

export interface ApprovePaymentResponse {
  success: boolean;
  message: string;
  data: {
    paymentId: number;
    status: 'approved';
    subscriptionId: number;
    approvedAt: string;
  };
}

export interface RejectPaymentRequest {
  adminId: number;
  rejectionReason?: string;
}

export interface RejectPaymentResponse {
  success: boolean;
  message: string;
  data: {
    paymentId: number;
    status: 'rejected';
    rejectedAt: string;
    rejectionReason?: string;
  };
}

// ─── Invoice Types ────────────────────────────────────────────────────────────

export interface InvoiceRecord {
  id: number;
  orderId: string;
  planName: string;
  amount: number;
  transactionId: string;
  paymentDate: string;
  subscriptionStatus: SubscriptionStatus;
}

export interface GetUserInvoicesParams {
  page?: number;
  limit?: number;
}

export interface GetUserInvoicesResponse {
  success: boolean;
  data: {
    invoices: InvoiceRecord[];
    pagination: PaginationMeta;
  };
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as any).response?.data;
    if (data?.message) return data.message as string;
    if (data?.error) return data.error as string;
  }
  return fallback;
}

// ─── Order API Client ─────────────────────────────────────────────────────────

export const orderAPI = {
  /**
   * Create a new order when a user selects a plan.
   * POST /api/orders/create
   * Requirements: 1.1, 1.2, 1.3
   */
  createOrder: async (data: CreateOrderRequest): Promise<CreateOrderResponse> => {
    try {
      const response = await api.post<CreateOrderResponse>('/orders/create', data);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to create order'));
    }
  },

  /**
   * Retrieve order details by order ID.
   * GET /api/orders/:orderId
   * Requirements: 1.1, 1.2, 1.3
   */
  getOrderById: async (orderId: string): Promise<GetOrderResponse> => {
    try {
      const response = await api.get<GetOrderResponse>(`/orders/${orderId}`);
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to fetch order'));
    }
  },
};

// ─── Payment API Client ───────────────────────────────────────────────────────

export const paymentAPI = {
  /**
   * Submit payment details with optional payment proof file.
   * POST /api/payments/submit  (multipart/form-data)
   * Requirements: 3.1, 3.2, 3.3, 10.1
   */
  submitPayment: async (data: SubmitPaymentRequest): Promise<SubmitPaymentResponse> => {
    try {
      const formData = new FormData();
      formData.append('orderId', data.orderId);
      formData.append('transactionId', data.transactionId);
      formData.append('paymentMethod', data.paymentMethod);
      if (data.paymentProof) {
        formData.append('paymentProof', data.paymentProof);
      }

      const response = await api.post<SubmitPaymentResponse>('/payments/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to submit payment'));
    }
  },

  /**
   * Get the authenticated user's payment history with optional pagination and status filter.
   * GET /api/payments/user
   * Requirements: 3.1, 3.2, 3.3, 10.1
   */
  getUserPayments: async (params: GetUserPaymentsParams = {}): Promise<GetUserPaymentsResponse> => {
    try {
      const response = await api.get<GetUserPaymentsResponse>('/payments/user', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to fetch payment history'));
    }
  },
};

// ─── Admin API Client ─────────────────────────────────────────────────────────

export const adminPaymentAPI = {
  /**
   * Get all payment requests with optional filters (admin only).
   * GET /api/admin/payments
   * Requirements: 7.1, 8.1, 9.1
   */
  getAllPayments: async (params: GetAllPaymentsParams = {}): Promise<GetAllPaymentsResponse> => {
    try {
      const response = await api.get<GetAllPaymentsResponse>('/admin/payments', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to fetch payment requests'));
    }
  },

  /**
   * Approve a payment request and activate the associated subscription (admin only).
   * PATCH /api/admin/payments/:id/approve
   * Requirements: 7.1, 8.1, 9.1
   */
  approvePayment: async (
    paymentId: number,
    data: ApprovePaymentRequest,
  ): Promise<ApprovePaymentResponse> => {
    try {
      const response = await api.patch<ApprovePaymentResponse>(
        `/admin/payments/${paymentId}/approve`,
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to approve payment'));
    }
  },

  /**
   * Reject a payment request (admin only).
   * PATCH /api/admin/payments/:id/reject
   * Requirements: 7.1, 8.1, 9.1
   */
  rejectPayment: async (
    paymentId: number,
    data: RejectPaymentRequest,
  ): Promise<RejectPaymentResponse> => {
    try {
      const response = await api.patch<RejectPaymentResponse>(
        `/admin/payments/${paymentId}/reject`,
        data,
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to reject payment'));
    }
  },
};

// ─── Invoice API Client ───────────────────────────────────────────────────────

export const invoiceAPI = {
  /**
   * Get the authenticated user's invoice history with optional pagination.
   * GET /api/invoices/user
   * Requirements: 11.1
   */
  getUserInvoices: async (params: GetUserInvoicesParams = {}): Promise<GetUserInvoicesResponse> => {
    try {
      const response = await api.get<GetUserInvoicesResponse>('/invoices/user', { params });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to fetch invoices'));
    }
  },
};
